/**
 * Owns one record node: a collection whose keys are data.
 *
 * Where a positional collection makes structure follow the value, here structure follows a
 * **declaration**. `upsert` brings a row into being and `remove` ends it; mounting a control does
 * neither. That single rule is what lets a table render column by column — the controls of one row
 * are mounted apart and at different times, and they come and go for reasons that have nothing to do
 * with the data: a row leaves edit mode, a sort re-renders, a filter hides it.
 *
 * Three consequences worth stating, because they are what the rule buys:
 *
 * - **Validity belongs to the declared row.** Validators are registered when the row is declared, so
 *   a form holding an invalid row stays invalid however few of its controls are on screen.
 * - **A control that mounts on an undeclared key claims nothing.** Its claim waits, it renders empty,
 *   and it binds when the key arrives — it never brings the row into being on its own.
 * - **Removing a row takes its value.** Claims still held by mounted controls go back to waiting.
 *
 * The engine enforces the first two through the gate this manager registers; see
 * `MdyFormEngine.registerPathGate`.
 */
import { MDY_DEV } from "./dev-flags.js";
import { isSafeFieldPath } from "./path-utils.js";
import { reactivityBatches, } from "./reactivity-contract.js";
import { isRecord } from "./record-utils.js";
import { destroyNestedUnder, nestedAt, registerRowNode, rowDeclaresCell } from "./collections/register.js";
/**
 * Refuses a shape the runtime cannot execute, when the form is built rather than when a row arrives.
 *
 * There is no depth to refuse and no combination of collections to refuse: a row may hold fields,
 * groups and collections of either kind, and a collection inside a row is addressed by the pattern
 * its declaration has — `orders.*.lines`, `items.*` — which is what makes a second positional level
 * as addressable as the first. What is still walked is the *kind* of every node, so a shape that is
 * not a form at all fails at construction rather than at the first row.
 */
function assertRowShape(node) {
    // Walked over an explicit stack, for the reason the document parser is
    // ([ADR 0043](../../../docs/architecture/0043-a-collection-nests-without-a-limit.md)): nesting has
    // no cap, so a recursive walk lets the shape decide how much stack to use. A tree of five thousand
    // collections — which the parser accepts, because it was built not to overflow — reached here and
    // raised `RangeError: Maximum call stack size exceeded`, which carries no path, cannot be caught by
    // name, and looks exactly like a defect in the caller's own code.
    const stack = [node];
    while (stack.length > 0) {
        const current = stack.pop();
        if (current.kind === "array" || current.kind === "record") {
            stack.push(current.item);
            continue;
        }
        if (current.kind === "group") {
            for (const child of Object.values(current.children))
                stack.push(child);
            continue;
        }
        if (current.kind !== "field") {
            throw new Error(`[modyra] A row may hold a field, a group or a collection — not ${String(current.kind)}`);
        }
    }
}
/** Names a rejected argument in a diagnostic without printing a whole payload. */
function describe(value) {
    if (value === null)
        return "null";
    if (Array.isArray(value))
        return "an array";
    return `a ${typeof value}`;
}
export class MdyRecordManager {
    collectionKind = "record";
    _deps;
    _initial;
    _keysSig;
    /**
     * The same key set the signal carries, kept as a plain set because the gate is consulted from
     * inside the engine's write paths, where reading a signal would tie an unrelated computation to
     * the shape of this collection.
     */
    _declared = new Set();
    _releaseGate;
    /**
     * One manager per collection declared inside a row, keyed by its full path.
     *
     * They belong to the row, not to the form: the row declared them and they go when it goes. A
     * form-level registry would have to work that ownership back out of a path, which is the thing
     * ADR 0040 says not to do.
     */
    _nested = new Map();
    /** The declared keys, in declaration order. */
    keys;
    constructor(deps, initial) {
        assertRowShape(deps.item);
        this._deps = deps;
        this._initial = initial;
        this._keysSig = deps.rx.signal([]);
        this.keys = this._keysSig.asReadonly();
        this._releaseGate = deps.engine.registerPathGate(deps.path, {
            isOpen: (name) => {
                if (name === deps.path)
                    return true;
                return this._declared.has(this._keyOf(name));
            },
            // A value written straight into the engine — a draft coming back, an undo that crosses the
            // moment a row was added — is this collection's own data returning. Declaring the row here is
            // what keeps a restore from quietly losing it; a control mounting still declares nothing.
            onRefusedWrite: (name) => {
                const key = this._keyOf(name);
                if (key.length === 0)
                    return;
                // The path is the instruction, so an extra segment is one too: a draft is written flat and
                // read back flat, and `lines.a.b.sku` asks for a member of a row that no template describes.
                // Built anyway, the collection holds a row whose shape its own document never declared and
                // the form calls itself valid, because there is no field there to be invalid.
                const rest = name.slice(`${deps.path}.${key}`.length + 1);
                if (!rowDeclaresCell(deps.item, rest)) {
                    if (MDY_DEV) {
                        console.warn(`[modyra] Ignored "${name}": a row of "${deps.path}" declares no member at ` +
                            `"${rest}", so the path describes a row this collection cannot hold.`);
                    }
                    return;
                }
                this.upsert(key);
            },
            // A whole-value write says which rows there are, so one it does not mention is one the user
            // removed before it was written — restoring it would undo their deletion.
            onReplace: (paths) => {
                // Insertion order, because a whole value says which rows there are *and* what order they are
                // in. The paths arrive in the order the value holds them.
                const present = new Set();
                for (const path of paths) {
                    const key = this._keyOf(path);
                    if (key.length > 0)
                        present.add(key);
                }
                for (const key of [...this._declared]) {
                    if (!present.has(key))
                        this.remove(key);
                }
                // A row this write brought back was declared again, and a row declared again arrives last.
                // A snapshot is a state the form was in, order included: undoing a removal has to put the
                // row back where it was, not at the end of a list the user never saw in that order.
                const order = [...present].filter((key) => this._declared.has(key));
                if (order.length === this._declared.size) {
                    this._keysSig.set(order);
                    this._reorderDeclared(order);
                    this._deps.engine.orderRowsUnder(this._deps.path, order);
                }
            },
        });
        // A phantom field at the record's own path, so record-level validator errors reach
        // `engine.errorsFor()` — the same reason the array manager registers one.
        deps.engine.getField(deps.path);
        this.setAll(initial);
    }
    /**
     * The set is what answers; the signal is what makes the answer live.
     *
     * `_declared` is deliberately a plain set — the gate reads it from the engine's write paths, where
     * touching a signal would tie an unrelated computation to this collection's shape. That is right
     * for the gate and wrong for a caller: every other member of the handle re-evaluates when its
     * answer changes, and one that did not would be read once in a template and never again.
     */
    has(key) {
        this._keysSig();
        return this._declared.has(key);
    }
    /** Declares the row, or rewrites the value of one already declared. */
    upsert(key, value) {
        if (!this._acceptKey(key))
            return;
        // A row that does not exist yet has nothing to read back: every cell of it reads as `null`, and
        // handing those down would declare a row of nulls where the template says what a row starts as.
        // Left undefined, each cell takes the initial its descriptor declares.
        const rowValue = value !== undefined
            ? value
            : this._declared.has(key) ? this._readRow(key) : undefined;
        this._reportUndeclaredCells(`${this._deps.path}.${key}`, this._deps.item, rowValue);
        const isNew = !this._declared.has(key);
        if (isNew) {
            this._declared.add(key);
            if (this._bulkDepth > 0)
                this._bulkKeys = true;
            else
                this._keysSig.update((keys) => [...keys, key]);
        }
        try {
            // One row, one change. A row's cells are registered one at a time, and a runtime whose
            // computations run eagerly would otherwise re-read the form between two of them — seeing a row
            // that has some of its cells, which is a shape the schema does not describe and a read that
            // raises. Batching is asked for rather than assumed: a runtime without it runs as before.
            this._batched(() => {
                this._registerNode(`${this._deps.path}.${key}`, this._deps.item, rowValue, `${this._deps.path}.${key}`, this._deps.sections ?? []);
            });
        }
        catch (error) {
            // Reading the value can raise — a getter over a store that is not loaded, a proxy that
            // refuses — and the caller catching that would reasonably assume the row was not declared.
            // The key goes back, so `keys()` and `getValue()` cannot disagree about a row that half
            // arrived. A key that was already there keeps the row it had, which is what a rewrite that
            // never reached a write leaves behind anyway.
            if (isNew)
                this.remove(key);
            throw error;
        }
        // Admits the waiting claims of controls that mounted before this row was declared — after the
        // row has registered its own fields, so that the row's shape is the template's and not the
        // order in which controls happened to arrive. A value whose keys follow the rendering is a
        // value the rendering can be read out of.
        if (isNew) {
            if (this._bulkDepth > 0)
                this._bulkDeclared = true;
            else
                this._deps.engine.refreshPathGate(this._deps.path);
        }
    }
    /**
     * Ends the row. Its value goes with it, and controls still mounted on it go back to waiting —
     * deletion is the owner's word, and a mounted control neither prevents it nor survives it.
     */
    /**
     * A collection inside a row: declared when the row is, destroyed with it.
     *
     * Re-declaring replaces what is there. A row rewritten by `upsert` runs the whole visit again,
     * and two managers over one path would both answer the gate for it.
     */
    _declareNested(path, node, value) {
        // The subtree being replaced ends with the declaration that held it: its manager goes, and so do
        // the fields it registered. Left behind, they are read back as rows by the reconciliation and the
        // old list reappears under the new one.
        const replaced = this._nested.get(path);
        if (replaced) {
            const leaves = replaced.leafPathsNow();
            const collections = replaced.collectionPathsNow();
            replaced.destroy();
            for (const leaf of leaves)
                this._deps.engine.endField(leaf);
            // And the field each collection below registered under its own path. It is not a leaf, so the
            // line above does not reach it, and one left behind is a row the reconciliation declares
            // again — the row comes back empty under the list that replaced it.
            for (const nested of collections)
                this._deps.engine.endField(nested);
        }
        const kind = node.kind;
        this._nested.set(path, this._deps.createCollection(kind, {
            rx: this._deps.rx,
            engine: this._deps.engine,
            path,
            item: node.item,
            warn: this._deps.warn,
            // Everything the row answers to, and the row itself: a collection inside a row that is no
            // longer declared is out of play with it.
            sections: [
                ...(this._deps.sections ?? []),
                () => this._declared.has(this._keyOf(path)),
            ],
            positionalAncestor: this._deps.positionalAncestor ?? false,
            createCollection: this._deps.createCollection,
        }, value));
    }
    /** Every collection path below this one — see {@link MdyNestedCollection.collectionPathsNow}. */
    collectionPathsNow() {
        return [...this._nested].flatMap(([path, manager]) => [path, ...manager.collectionPathsNow()]);
    }
    /** Every leaf path of every declared row — what an enclosing collection treats as its fields. */
    leafPathsNow() {
        return this.keysNow().flatMap((key) => this._leafPaths(`${this._deps.path}.${key}`, this._deps.item));
    }
    /** The manager for a collection declared inside one of these rows, wherever it sits below. */
    nested(path) {
        return nestedAt(this._nested, path);
    }
    /** Everything a row owned, the collections it declared included. */
    _destroyNestedUnder(prefix) {
        destroyNestedUnder(this._nested, this._deps.engine, prefix);
    }
    remove(key) {
        if (!this._declared.delete(key))
            return;
        // One change, like the declaration is: a row ends cell by cell, and a runtime whose computations
        // run eagerly reads the form between two of them — finding a row holding some of its cells,
        // which is a shape the schema does not describe and a read that raises.
        this._batched(() => {
            this._destroyNestedUnder(`${this._deps.path}.${key}`);
            if (this._bulkDepth > 0) {
                this._bulkKeys = true;
                this._bulkDeclared = true;
            }
            else {
                this._keysSig.update((keys) => keys.filter((k) => k !== key));
                this._deps.engine.refreshPathGate(this._deps.path);
            }
        });
    }
    /**
     * Declares exactly these keys, removing every other.
     *
     * Handed something that is not an object — the `undefined` a response can carry — it declares
     * nothing and says so. Emptying the collection stays possible and stays deliberate: `setAll({})`.
     */
    /**
     * How many bulk writes are in progress, and whether one of them declared a row.
     *
     * Declaring a row re-reads the gate over this collection, which walks every claim and every field
     * the form holds: one row's worth of work is small, and one per row of a two-thousand-row write is
     * the whole cost of the write. A bulk write does it once, at the end, which is the same state — a
     * gate re-read admits waiting claims, and nothing between the rows of one write can be looking.
     */
    _bulkDepth = 0;
    _bulkDeclared = false;
    /**
     * Whether the published key list is behind the declared set.
     *
     * The list is a value, so adding one key copies it: a two-thousand-row write copied a growing
     * array two thousand times, which is the same cost the gate re-read had. The set is the answer
     * `has()` gives and it is already up to date; the list is published once, from it.
     */
    _bulkKeys = false;
    /** Runs `write` as one bulk write: the gate is re-read once, when the outermost one finishes. */
    _asBulk(write) {
        this._bulkDepth += 1;
        try {
            write();
        }
        finally {
            this._bulkDepth -= 1;
            if (this._bulkDepth === 0 && this._bulkKeys) {
                this._bulkKeys = false;
                // Insertion order is declaration order, which is the order the incremental writes produced.
                this._keysSig.set([...this._declared]);
            }
            if (this._bulkDepth === 0 && this._bulkDeclared) {
                this._bulkDeclared = false;
                this._deps.engine.refreshPathGate(this._deps.path);
            }
        }
    }
    setAll(values) {
        if (!isRecord(values) || Array.isArray(values)) {
            this._warn(`setAll on "${this._deps.path}" ignored ${describe(values)}: it takes an object keyed by row ` +
                "key. Pass {} to empty the collection.");
            return;
        }
        const wanted = values;
        // One call, one change. Row by row, undoing a three-row write took three presses and showed a
        // table with some rows written and some not at each of them — states the collection was never
        // in. `patch` on the same handle already answered as one.
        this._deps.engine.mutate(() => {
            this._asBulk(() => {
                for (const key of [...this._declared]) {
                    if (!(key in wanted))
                        this.remove(key);
                }
                for (const [key, value] of Object.entries(wanted)) {
                    this.upsert(key, value);
                }
            });
        });
    }
    /**
     * Writes several rows in one call. A key that is not declared yet is declared here: a patch on a
     * record comes from whoever owns the keys, which is the one party allowed to say a row exists.
     */
    /** {@link MdyNestedCollection.patchFrom} for a keyed collection: the rows a patch names, merged. */
    patchFrom(value) {
        if (isRecord(value) && !Array.isArray(value))
            this.patch(value);
    }
    patch(values) {
        if (!isRecord(values) || Array.isArray(values)) {
            this._warn(`patch on "${this._deps.path}" ignored ${describe(values)}: it takes an object keyed by row ` +
                "key — { key: { field: value } }.");
            return;
        }
        for (const [key, value] of Object.entries(values)) {
            if (this._deps.item.kind === "group" && !isRecord(value)) {
                this._warn(`patch on "${this._deps.path}.${key}" ignored ${describe(value)}: this row is a group, so ` +
                    "a patch names its fields — { field: value }.");
                continue;
            }
            if (!this._declared.has(key)) {
                this.upsert(key, value);
                continue;
            }
            this._writeInto(`${this._deps.path}.${key}`, this._deps.item, value);
        }
    }
    /**
     * Moves a row to a new key, carrying value, validity and `touched`.
     *
     * `remove` followed by `upsert` reaches the same value; what only this can keep is the state the
     * user produced — a field they visited stays visited. Renaming onto an existing key is refused,
     * because the row that key already names would be silently replaced.
     */
    rename(from, to) {
        if (!this._declared.has(from)) {
            this._warn(`rename on "${this._deps.path}" ignored: there is no row "${from}" to move.`);
            return;
        }
        if (this._declared.has(to)) {
            this._warn(`rename on "${this._deps.path}" ignored: "${to}" already names a row, and moving onto it ` +
                `would replace it. Remove "${to}" first if that is what you mean.`);
            return;
        }
        if (!this._acceptKey(to))
            return;
        const value = this._readRow(from);
        const flags = this._readFlags(from);
        // The row's leaves, before and after: what a binder said about a cell moves with the row, like
        // its value and its marks. Read before the removal, because removing the row releases them.
        const leaves = this._leafPaths(`${this._deps.path}.${from}`, this._deps.item)
            .map((path) => path.slice(`${this._deps.path}.${from}`.length));
        // Moved before the removal: ending the row releases what a binder said about its cells, and the
        // row is not ending — it is arriving under another key.
        this._deps.engine.carryBindings(leaves.map((suffix) => [`${this._deps.path}.${from}${suffix}`, `${this._deps.path}.${to}${suffix}`]));
        // Where the row sits among its siblings, before it moves. A rename changes a row's key, not its
        // place in the list a person is looking at, and `remove` + `upsert` — which is how the move is
        // carried out below — appends. Renaming the second of five would otherwise send it to the
        // bottom, which is the one difference from remove-then-upsert nobody asked for.
        const at = this._keysSig().indexOf(from);
        // The row leaves one key and arrives at another as one change: between the two it exists under
        // neither, and an eager runtime reading there sees a form the schema cannot describe.
        this._batched(() => {
            this.remove(from);
            this.upsert(to, value);
            this._writeFlags(to, flags);
            this._keysSig.update((keys) => {
                const others = keys.filter((key) => key !== to);
                return [...others.slice(0, at), to, ...others.slice(at)];
            });
            this._reorderDeclared(this.keysNow());
            // And the value reads its rows in the order their fields were registered, which the move has
            // just changed. Both answer for the same list, so both follow the keys.
            this._deps.engine.orderRowsUnder(this._deps.path, this.keysNow());
        });
    }
    /** Runs `write` as one change where the runtime can, and plainly where it cannot. */
    _batched(write) {
        const rx = this._deps.rx;
        if (reactivityBatches(rx))
            rx.batch(write);
        else
            write();
    }
    /** Every declared row's value, read back from the engine. */
    getValues() {
        const out = {};
        for (const key of this._declared)
            out[key] = this._readRow(key);
        return out;
    }
    /** True when every field of the row passes — a row nobody mounted answers as truly as one on screen. */
    validOf(key) {
        // Read before the early return: an undeclared key reads no field signal, so without this the
        // answer would be computed once, when it was false, and stay false after the row arrived.
        this._keysSig();
        if (!this._declared.has(key))
            return false;
        return this._leafPaths(`${this._deps.path}.${key}`, this._deps.item).every((path) => {
            const ref = this._deps.engine.peekField(path);
            return ref ? ref().valid() : true;
        });
    }
    /** Declares the keys the schema started with. */
    resetToInitial() {
        this.setAll(this._initial);
    }
    /** Releases the gate — call when the owning form is destroyed. */
    destroy() {
        for (const manager of this._nested.values())
            manager.destroy();
        this._nested.clear();
        this._releaseGate();
    }
    /**
     * The leaves a row offers, relative to the row: `["name", "inner.deep"]`, or `[""]` when the rows
     * are leaves themselves. What `cell()` is allowed to address, and what a diagnostic can suggest.
     */
    rowLeaves() {
        const prefix = `${this._deps.path}.`;
        return this._leafPaths(this._deps.path, this._deps.item).map((leaf) => leaf.startsWith(prefix) ? leaf.slice(prefix.length) : "");
    }
    /** True when `path` (relative to a row, `undefined` for a leaf row) addresses one of them. */
    addresses(path) {
        return this.rowLeaves().includes(path ?? "");
    }
    // ── Internals ──────────────────────────────────────────────────────────────
    _warn(message) {
        if (MDY_DEV)
            this._deps.warn(message);
    }
    /** The row key a path below this collection belongs to. */
    _keyOf(path) {
        // Read without allocating: this answers the gate for every field of every form the collection
        // shares an engine with, once per row of a bulk write, and `split` built a whole array to throw
        // away all but its first member.
        const start = this._deps.path.length + 1;
        const end = path.indexOf(".", start);
        return end === -1 ? path.slice(start) : path.slice(start, end);
    }
    /**
     * A key is a path segment, so it may not carry the separator, and it inherits the path grammar
     * that keeps `__proto__` and friends out. A rejected key is reported and dropped rather than
     * thrown, because keys arrive from outside — a server row should not take the form down.
     */
    _acceptKey(key) {
        if (key.length > 0 && !key.includes(".") && isSafeFieldPath(`${this._deps.path}.${key}`)) {
            return true;
        }
        this._warn(`Ignored record key ${JSON.stringify(key)} on "${this._deps.path}": a key must be non-empty, ` +
            "must not contain \".\", and must not be a prototype-polluting name.");
        return false;
    }
    /** The row a path belongs to, as the value an enclosing condition reads. */
    _rowValue(rowPath) {
        const row = this._readNode(rowPath, this._deps.item);
        return isRecord(row) ? row : {};
    }
    _registerNode(fullPath, rowNode, value, rowPath, sections = []) {
        registerRowNode(this._registration, fullPath, rowNode, value, rowPath, sections);
    }
    /** What the shared visit needs from this manager, built once. */
    get _registration() {
        return {
            engine: this._deps.engine,
            rx: this._deps.rx,
            readRow: (rowPath) => this._readNode(rowPath, this._deps.item),
            readNode: (path, node) => this._readNode(path, node),
            rowValue: (rowPath) => this._rowValue(rowPath),
            // A clause written in a row's template cannot name the row's key, so what encloses it is the
            // row; this is the way back out to the form, which is what `{ root: true }` reads.
            readRoot: this._deps.readRoot,
            onCollection: (path, node, value) => {
                this._declareNested(path, node, value);
            },
        };
    }
    /**
     * Says which members of a written row this collection's template does not declare.
     *
     * A row's shape is the template's, so a key the template has no child for is a write that does
     * nothing — and the form shows what it already held, which is indistinguishable from the write
     * having landed. The doors this reaches are the ones where the keys come from data: a server
     * response, a saved project, a document.
     */
    _reportUndeclaredCells(fullPath, rowNode, value) {
        if (!MDY_DEV || rowNode.kind !== "group" || !isRecord(value))
            return;
        const declared = rowNode.children;
        const unknown = Object.keys(value).filter((key) => !(key in declared));
        if (unknown.length === 0)
            return;
        this._warn(`write to "${fullPath}" ignored ${unknown.map((key) => `"${key}"`).join(", ")}: a row of ` +
            `"${this._deps.path}" declares no such member.`);
    }
    /** Writes a partial row without re-registering it — the row keeps its validators and its flags. */
    _writeInto(fullPath, rowNode, value) {
        if (rowNode.kind === "field") {
            const ref = this._deps.engine.peekField(fullPath);
            if (ref)
                ref().value.set(value);
            return;
        }
        // A collection is written by its own manager rather than walked into here — as a patch, because
        // this is the patch door: a row of it that names some cells keeps the ones it does not name.
        if (rowNode.kind === "record" || rowNode.kind === "array") {
            this._nested.get(fullPath)?.patchFrom(value);
            return;
        }
        if (!isRecord(value))
            return;
        this._reportUndeclaredCells(fullPath, rowNode, value);
        for (const [key, child] of Object.entries(rowNode.children)) {
            if (!(key in value))
                continue;
            const at = `${fullPath}.${key}`;
            if (child.kind === "record" || child.kind === "array") {
                // The value names this collection, so what it carries is the whole list of rows — a row it
                // omits is gone. What it does not say is what the rows it does carry hold in the cells they
                // leave out, and a patch leaves those alone.
                this._nested.get(at)?.patchFrom(value[key]);
                continue;
            }
            this._writeInto(at, child, value[key]);
        }
    }
    _leafPaths(fullPath, rowNode) {
        if (rowNode.kind === "record" || rowNode.kind === "array") {
            return this._nested.get(fullPath)?.leafPathsNow() ?? [];
        }
        if (rowNode.kind === "field")
            return [fullPath];
        const group = rowNode;
        return Object.entries(group.children).flatMap(([key, child]) => this._leafPaths(`${fullPath}.${key}`, child));
    }
    /**
     * Replaces the rows wholesale, from whatever the enclosing write carried.
     *
     * A value that is not a record says nothing about keys, so it changes nothing: a whole-row write
     * that names this collection with the wrong shape is a defect upstream, not an instruction to
     * empty it.
     */
    setAllFrom(value) {
        if (isRecord(value))
            this.setAll(value);
    }
    /**
     * Puts the declared set in the order the list reports.
     *
     * Two operations move a key without adding or removing one — an undo that puts a row back where it
     * was, and a rename that gives a row the old key's place — and both wrote the new order into the
     * list only. The set kept the order the keys were first declared in, and the set is what a whole
     * value and a bulk write read: `keys()` said the row had moved and the next `setAll` put it back,
     * arbitrarily far from the operation that caused it. One order, in both places.
     */
    _reorderDeclared(order) {
        if (order.length !== this._declared.size)
            return;
        this._declared.clear();
        for (const key of order)
            this._declared.add(key);
    }
    /** The declared keys as a plain array, for a sibling manager reading through this one. */
    keysNow() {
        // The order `keys()` answers, not the order the set happens to hold: `_declared` is membership,
        // the signal is the list. A rename moves a row within the list without leaving and re-entering
        // the set, so the two differ exactly where the order is what is being asked about. Untracked
        // because this is the answer now, for a caller that is not recomputing.
        return this._deps.rx.untracked(() => this._keysSig());
    }
    _readRow(key) {
        return this._readNode(`${this._deps.path}.${key}`, this._deps.item);
    }
    _readNode(fullPath, rowNode) {
        if (rowNode.kind === "record" || rowNode.kind === "array") {
            // Through the manager that owns it: the rows are its answer, not something derivable from
            // the declaration, which names no keys.
            const nested = this._nested.get(fullPath);
            if (nested)
                return nested.getValues();
            return rowNode.kind === "array" ? [] : {};
        }
        if (rowNode.kind === "field") {
            const ref = this._deps.engine.peekField(fullPath);
            return ref ? ref().value() : null;
        }
        const out = {};
        const group = rowNode;
        for (const [key, child] of Object.entries(group.children)) {
            out[key] = this._readNode(`${fullPath}.${key}`, child);
        }
        return out;
    }
    _readFlags(key) {
        const prefix = `${this._deps.path}.${key}`;
        const out = new Map();
        for (const path of this._leafPaths(prefix, this._deps.item)) {
            const ref = this._deps.engine.peekField(path);
            if (!ref)
                continue;
            const state = ref();
            out.set(path.slice(prefix.length), { touched: state.touched(), dirty: state.dirty() });
        }
        return out;
    }
    _writeFlags(key, flags) {
        const prefix = `${this._deps.path}.${key}`;
        for (const [suffix, flag] of flags) {
            const ref = this._deps.engine.peekField(`${prefix}${suffix}`);
            if (!ref)
                continue;
            const state = ref();
            state.touched.set(flag.touched);
            state.dirty.set(flag.dirty);
        }
    }
}
